/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import config from '../../../../share/config';
import session from '../../../../share/session';

/** 各フェーズ定義 */
const PHASE = {
  START_CONFIRM: 'start_confirm',                          // 開始確認
  AIMING_EXE: 'aiming_exe',                                // エイミング 実行監視画面
  AIMING_SUCCESS: 'aiming_success',                        // エイミング 正常終了表示画面
  AIMING_ABNORMAL: 'aiming_abnormal',                      // エイミング 異常終了表示画面
  START_DISTANCE_CORRECTION: 'start_distance_correction',  // 距離補正 待機画面
  DISTANCE_CORRECTION_EXE: 'distance_correction_exe',      // 距離補正 実行監視画面
  END_DISTANCE_CORRECTION: 'end_distance_correction',      // 距離補正 停止画面
  ACCURACY_RATE_TEST: 'accuracy_rate_test',                // 正解率検査 待機画面
  ACCURACY_RATE_TEST_EXE: 'accuracy_rate_test_exe',        // 正解率検査 実行監視画面
  END_ACCURACY_RATE_TEST: 'end_accuracy_rate_test',        // 正解率検査 停止画面
  TIMEOUT_ERROR: 'timeout_error',                          // タイムアウトエラー
  SERVICE_MANUAL_ERROR: 'service_manual_error',            // サービスマニュアルエラー
  FAILED: 'failed',                                        // 通信エラー
  END_CONFIRMATION: 'end_confirmation',                    // 終了確認
  DANGER: 'danger',                                        // RoutineStatus値によるエラー表示用Phase
};

/** 固有のエラー表示用タイプ */
const RESULT_TYPE = {
  SUCCESS: 'success',
  SERVICE_MANUAL_ERROR: 'service_manual_error',
  TIMEOUT_ERROR: 'timeout_error',
  COM_ERROR: 'communication_error',
  FAILED: 'failed'
};

export default {
  name: 'requirement056',
  data() {
    return {
      phase: PHASE.START_CONFIRM,
      beforePhase: PHASE.START_CONFIRM,
      responseValue: '',
      isLoading: false,
      displayInfo: '',
      systemId: '',
      // ダイアログフラグ
      modal_requirement_056: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),

    /** ダイアログのヘッダー色 */
    headerColor() {
      switch (this.phase) {
        case PHASE.AIMING_ABNORMAL:
        case PHASE.TIMEOUT_ERROR:
        case PHASE.SERVICE_MANUAL_ERROR:
        case PHASE.FAILED:
        case PHASE.DANGER:
          return 'danger';
        case PHASE.AIMING_SUCCESS:
        case PHASE.END_DISTANCE_CORRECTION:
        case PHASE.END_ACCURACY_RATE_TEST:
          return 'success';
        default:
          return 'secondary';
      }
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_requirement_056 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      this.displayInfo = this.$t(`${this.i18nWsKey}.REQ_WS_56_TXT_1`);
    },

    /**
     * 開始確認 OKボタン押下
     */
    async startConfirm() {
      try {
        this.isLoading = true;
        // 初期化(ポーリング開始)
        const response = await this.getPolling('execute', 'init');
        if (response) {
          // ポーリングが正常に完了した場合
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 正常終了(RoutineStatus=0x1002)
              if (response.data.user_data.result_data === RESULT_TYPE.SUCCESS) {
                // エイミング実行監視画面表示
                this.phase = PHASE.AIMING_EXE;
                this.displayInfo = this.$t(`${this.i18nWsKey}.REQ_WS_56_TXT_2_1001`);
                // エイミング処理実行
                await this.aiming();
              } else if (response.data.user_data.result_data === RESULT_TYPE.TIMEOUT_ERROR) {
                // タイムアウト(RoutineStatus=0x1001)
                this.timeoutError();
              } else {
                // 通信エラー(上記以外のRoutineStatus)
                this.communicationError();
              }
              break;
            case config.STATUS_WSP_FAILED:
            default:
              this.communicationError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** エーミング実行 */
    async aiming() {
      // 本処理はstartConfirmから呼ばれる為、スピナー処理は不要
      // エーミング(ポーリング開始)
      const response = await this.getPolling('execute', 'aiming');
      if (response) {
        // ポーリングが正常に完了した場合
        switch (response.data.status) {
          case config.STATUS_WSP_COMPLETED: {
            const routineStatus = response.data.user_data.routine_status;
            const resultData = response.data.user_data.result_data;
            const accuracy_rate = response.data.user_data.accuracy_rate;

            // 正常終了(RoutineStatus=0x1400~0x140E, 0x1410)
            if (resultData === RESULT_TYPE.SUCCESS) {
              // RoutineStatusに合った表示文言を取得
              const displayInfoWord = this.$t(`${this.i18nWsKey}.REQ_WS_56_TXT_3_${routineStatus}`);
              // RoutineStatusが0x1400 または 0x1410 の場合は対象の表示文言に正解率値の置換処理を実施する(他は不要)
              if (routineStatus === '1400') {
                this.displayInfo = displayInfoWord.replace("**", accuracy_rate);
                this.phase = PHASE.AIMING_SUCCESS;
              } else if (routineStatus === '1410') {
                this.displayInfo = displayInfoWord.replace("**", accuracy_rate);
                this.phase = PHASE.DANGER;
              } else {
                this.displayInfo = displayInfoWord;
                this.phase = PHASE.DANGER;
              }
            } else if (resultData === RESULT_TYPE.FAILED) {
              // 異常終了(RoutineStatus=0x140F)
              this.phase = PHASE.AIMING_ABNORMAL;
              this.displayInfo = this.$t(`${this.i18nWsKey}.REQ_WS_56_TXT_4_140F`);
            } else if (resultData === RESULT_TYPE.TIMEOUT_ERROR) {
              // タイムアウト(RoutineStatus=0x1001)
              this.timeoutError();
            } else {
              // 上記以外 - サービスマニュアルエラー
              this.serviceManualError(routineStatus);
            }
            break;
          }
          case config.STATUS_WSP_FAILED:
          default:
            this.communicationError();
            break;
        }
      }
    },

    /** 距離補正開始確認 */
    async startDistanceCorrection() {
      try {
        this.isLoading = true;
        // 距離補正(ポーリング開始)
        const response = await this.getPolling('execute', 'correct');
        if (response) {
          // ポーリングが正常に完了した場合
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED: {
              const routineStatus = response.data.user_data.routine_status;
              const resultData = response.data.user_data.result_data;
              // 次の停止表示後の処理に使用する為、RoutineStatusを保持する
              this.responseValue = routineStatus;
              // 正常終了(RoutineStatus=0x1500, 0x1501)
              if (resultData === RESULT_TYPE.SUCCESS) {
                // HACK: Phase4Step2-2終了間際にESLintのルールが変更になり、ロジックは変更出来なかったため回避。
                /* eslint-disable max-depth */
                if (routineStatus === '1500') {
                  this.phase = PHASE.END_DISTANCE_CORRECTION;
                  this.displayInfo = this.$t(`${this.i18nWsKey}.REQ_WS_56_TXT_7_${routineStatus}`);
                } else if (routineStatus === '1501') {
                  this.phase = PHASE.DANGER;
                  this.displayInfo = this.$t(`${this.i18nWsKey}.REQ_WS_56_TXT_7_${routineStatus}`);
                }
              } else if (resultData === RESULT_TYPE.TIMEOUT_ERROR) {
                // タイムアウト(RoutineStatus=0x1001)
                this.timeoutError();
              } else {
                // 上記以外 - サービスマニュアルエラー
                this.serviceManualError(routineStatus);
              }
              break;
            }
            case config.STATUS_WSP_FAILED:
            default:
              this.communicationError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 正解率検査実行 */
    async accuracyRateTestExe() {
      try {
        this.isLoading = true;
        const response = await this.getPolling('execute', 'inspect');
        if (response) {
          // ポーリングが正常に完了した場合
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED: {
              const routineStatus = response.data.user_data.routine_status;
              const resultData = response.data.user_data.result_data;
              const accuracy_rate = response.data.user_data.accuracy_rate;

              // 正常終了(RoutineStatus=0x1300~0x1303)
              if (resultData === RESULT_TYPE.SUCCESS) {
                // RoutineStatusに合った表示文言を取得
                const displayInfoWord = this.$t(`${this.i18nWsKey}.REQ_WS_56_TXT_10_${routineStatus}`);
                // RoutineStatusが0x1300 または 0x1303 の場合は対象の表示文言に正解率値の置換処理を実施する(他は不要)
                // HACK: Phase4Step2-2終了間際にESLintのルールが変更になり、ロジックは変更出来なかったため回避。
                /* eslint-disable max-depth */
                if (routineStatus === '1300') {
                  this.displayInfo = displayInfoWord.replace("**", accuracy_rate);
                  this.phase = PHASE.END_ACCURACY_RATE_TEST;
                } else if (routineStatus === '1303') {
                  this.displayInfo = displayInfoWord.replace("**", accuracy_rate);
                  this.phase = PHASE.DANGER;
                } else {
                  this.displayInfo = displayInfoWord;
                  this.phase = PHASE.DANGER;
                }
              } else if (resultData === RESULT_TYPE.TIMEOUT_ERROR) {
                // タイムアウト(RoutineStatus=0x1001)
                this.timeoutError();
              } else {
                // 上記以外 - サービスマニュアルエラー
                this.serviceManualError(routineStatus);
              }
              break;
            }
            case config.STATUS_WSP_FAILED:
            default:
              this.communicationError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 正解率検査終了 */
    async callSuspension() {
      try {
        this.isLoading = true;
        await this.getPolling('suspension');
        this.closeWizard();
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.communicationError(); },
      );
    },

    /**
     * ポーリング処理.
     * @param {String} typeKey タイプキー
     * @param {String} processKey プロセスキー
     * @returns レスポンス
     */
    async getPolling(typeKey, processKey = undefined) {
      const token = session.getToken();
      const body = processKey === undefined ?
        { id_token: token.id_token, type: typeKey } : { id_token: token.id_token, type: typeKey, process: processKey };
      const response = await individualWorkSupportUtil.pollingWspApi(
        this,
        this.netAppId,
        this.systemId,
        this.workSupportId,
        body,
        // エラー発生のコールバックを実装する。
        // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列、もしくは独自定義した文字列が返ってくる。
        // 必要に応じて、errorType に応じてメッセージを変えるといったことも可能。
        (errorType) => { errorType; this.communicationError(); },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
        (result) => { this.validateErrorResult(result); },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
        (result) => { this.validateErrorResult(result); },
      );
      return response;
    },

    /** 終了確認画面遷移 */
    async moveEndConfirm() {
      // 遷移前に呼び出し元画面のPhaseを保持
      this.beforePhase = this.phase;
      // 終了確認画面に遷移
      this.phase = PHASE.END_CONFIRMATION;
      this.displayInfo = this.$t(`${this.i18nWsKey}.REQ_WS_56_TXT_11`);
    },

    /** 終了確認画面(キャンセルボタン押下)から前画面遷移 */
    async exitCancel() {
      switch (this.beforePhase) {
        // 開始確認画面 -> 終了確認画面 -> 開始確認画面
        case PHASE.START_CONFIRM:
          this.phase = PHASE.START_CONFIRM;
          this.displayInfo = this.$t(`${this.i18nWsKey}.REQ_WS_56_TXT_1`);
          break;
        // エーミング異常終了画面 -> 終了確認画面 -> エーミング異常終了画面
        case PHASE.AIMING_ABNORMAL:
          this.phase = PHASE.AIMING_ABNORMAL;
          this.displayInfo = this.$t(`${this.i18nWsKey}.REQ_WS_56_TXT_4_140F`);
          break;
        // 距離補正待機画面 -> 終了確認画面 -> 距離補正待機画面
        case PHASE.START_DISTANCE_CORRECTION:
          this.phase = PHASE.START_DISTANCE_CORRECTION;
          this.displayInfo = this.$t(`${this.i18nWsKey}.REQ_WS_56_TXT_5`);
          break;
        // 正解率検査待機画面 -> 終了確認画面 -> 正解率検査待機画面
        case PHASE.ACCURACY_RATE_TEST:
          this.phase = PHASE.ACCURACY_RATE_TEST;
          this.displayInfo = this.$t(`${this.i18nWsKey}.REQ_WS_56_TXT_8`);
          break;
      }
    },

    /** 終了処理遷移 */
    async endExit() {
      try {
        this.isLoading = true;
        // 処理結果は見ずにポーリングのみ開始
        if (this.phase === PHASE.END_CONFIRMATION) {
          // 終了確認画面且つ遷移元が開始確認画面以外の場合はルーチン停止・DefaultSession遷移を実施
          if (this.beforePhase !== PHASE.START_CONFIRM) {
            await this.getPolling('suspension');
          }
          // 終了確認画面且つ遷移元が開始確認画面の場合は何もせず、機能停止
        } else {
          // その他エラー画面から実行された場合はDefaultSession遷移を実施
          await this.getPolling('exit');
        }
        this.closeWizard();
      } finally {
        this.isLoading = false;
      }
    },

    /** エーミング異常終了画面OKボタン押下イベント(距離補正待機画面遷移) */
    async aimingAbnormalOKEvent() {
      this.phase = PHASE.START_DISTANCE_CORRECTION;
      this.displayInfo = this.$t(`${this.i18nWsKey}.REQ_WS_56_TXT_5`);
    },

    /** 距離補正待機画面OKボタン押下イベント(距離補正実行監視画面遷移 兼 距離補正API実行) */
    async startDistanceCorrectionOKEvent() {
      this.phase = PHASE.DISTANCE_CORRECTION_EXE;
      this.displayInfo = this.$t(`${this.i18nWsKey}.REQ_WS_56_TXT_6_1001`);
      await this.startDistanceCorrection();
    },

    /** 距離補正終了 */
    async endDistanceCorrection() {
      // 正解率検査に遷移
      this.phase = PHASE.ACCURACY_RATE_TEST;
      this.displayInfo = this.$t(`${this.i18nWsKey}.REQ_WS_56_TXT_8`);
    },

    /** 正解率検査待機画面OKボタン押下イベント(正解率検査実行監視画面遷移 兼 正解率検査API実行) */
    async accuracyRateTestOKEvent() {
      this.phase = PHASE.ACCURACY_RATE_TEST_EXE;
      this.displayInfo = this.$t(`${this.i18nWsKey}.REQ_WS_56_TXT_9_1001`);
      await this.accuracyRateTestExe();
    },

    /**タイムアウトエラー */
    timeoutError() {
      this.phase = PHASE.TIMEOUT_ERROR;
      this.displayInfo = this.$t(`${this.i18nWsKey}.REQ_WS_56_TXT_12`);
    },

    /** サービスマニュアルエラー */
    serviceManualError(routineStatus) {
      this.phase = PHASE.SERVICE_MANUAL_ERROR;
      this.displayInfo = this.$t(`${this.i18nWsKey}.REQ_WS_56_TXT_13`).replace('$ROUTINESTATUS$', routineStatus);
    },

    /** 通信エラー */
    communicationError() {
      this.phase = PHASE.FAILED;
      this.displayInfo = this.$t(`${this.i18nWsKey}.REQ_WS_56_TXT_14`);
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_056 = false;
    },
  },

  async mounted() {
    this.init();
  },
};